/* PTL → PTL Billing → Billing Overview.
   One billing month: the cycle rule, the diesel price (drives DPH), prepare / refresh of the billing lines, the
   cycle's KPIs and what is blocking billing, ad hoc reminders, and the billing settings. All amounts exclude GST. */
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Switch,
  FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import { kpiRowSx } from '../../common/sharedStyles'
import { useBillingConfigQuery, useBillingCycleQuery, useCycleSummaryQuery, useAdhocListQuery, usePrepareCycleMutation,
  useSetDieselPriceMutation, useUpdateBillingConfigMutation, useInTransitQuery, useLossClientsQuery, BILLING_BASE } from '../../redux/ptlBillingApiSlice'
import { apiConstants, getDownload } from '../../common/constants'
import KpiTile from './components/KpiTile'
import { canBill, dayOfNextMonth, downloadFile, errText, fmtDate, fmtDateTime, inr, monthLabel, pct, recentMonths, safeMonth } from './billingUtils'

export default function BillingOverview() {
  const [params, setParams] = useSearchParams()
  const month = safeMonth(params.get('month'))
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const billing = canBill()

  const { data: config, error: configError } = useBillingConfigQuery()
  const { data: cycle } = useBillingCycleQuery(month)
  const { data: summary, isFetching: summaryLoading, error: summaryError, refetch } = useCycleSummaryQuery(month)
  const { data: adhoc } = useAdhocListQuery({ cycleMonth: month, size: 1 })
  const { data: transit } = useInTransitQuery({ month, limit: 5 })
  const { data: losers = [] } = useLossClientsQuery(month)
  const [setDiesel, { isLoading: savingDiesel }] = useSetDieselPriceMutation()
  const [prepare, { isLoading: preparing }] = usePrepareCycleMutation()

  const [diesel, setDieselInput] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)
  useEffect(() => { setDieselInput(cycle?.dieselPrice ?? '') }, [cycle?.dieselPrice, month])
  // a closed month keeps its diesel price; one that never got a price can still get it once
  const dieselLocked = cycle?.status === 'CLOSED' && cycle?.dieselPrice != null

  // flag name → message, over the month's stored lines (same as Billing Review)
  const flagRows = useMemo(() => {
    if (!summary) return []
    const msg = summary.flagMessages || {}
    return Object.entries(summary.flagCounts || {}).filter(([, n]) => n > 0).map(([f, n]) => ({ flag: f, n, message: msg[f] || f }))
      .sort((a, b) => b.n - a.n)
  }, [summary])

  const saveDiesel = async () => {
    const res = await setDiesel({ month, dieselPrice: diesel === '' ? null : Number(diesel) })
    if (res.error) enqueueSnackbar(errText(res.error), { variant: 'error' })
    else enqueueSnackbar(`Diesel price for ${monthLabel(month)} saved`, { variant: 'success' })
  }
  const runPrepare = async () => {
    const res = await prepare({ month })
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    const d = res.data
    enqueueSnackbar(`Billing lines for ${monthLabel(month)}: ${d.created} new, ${d.refreshed} updated, ${d.unchanged} unchanged, `
      + `${d.frozen} approved (not touched), ${d.removed} no longer billable.`, { variant: 'success' })
  }

  const cutoff = config?.cutoffDay
  const closeDay = config?.closeDay
  // margin % over the lines with a known courier cost only; lines with no price or no courier cost are left out
  const margin = summary && summary.marginBaseTotal > 0 ? summary.margin / summary.marginBaseTotal : null
  const closed = cycle?.status === 'CLOSED'
  // months before the first month billed in Proship were billed outside it (Billing settings)
  const beforeStart = !!config?.firstBillingMonth && month < config.firstBillingMonth
  const noLines = summary && !summaryLoading && summary.shipments === 0
  const waiting = (adhoc?.waitingOps || 0) + (adhoc?.waitingKam || 0)

  return (
    <Box>
      <PageTitle>Billing Overview</PageTitle>

      <Card elevation={0} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size='small' label='Service month' value={month} sx={{ minWidth: 200 }}
            onChange={(e) => setParams({ month: e.target.value })}>
            {recentMonths(12).map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography sx={{ fontWeight: 600 }}>Service month: {monthLabel(month)}</Typography>
            {config && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Shipments picked up in {monthLabel(month)} and delivered (or lost) on or before {dayOfNextMonth(month, cutoff)}.
                Delivered later moves to the next month. Approvals close on {dayOfNextMonth(month, closeDay)}. All amounts exclude GST.
              </Typography>
            )}
          </Box>
          <Chip label={cycle?.status === 'CLOSED' ? 'CLOSED' : 'OPEN'} color={cycle?.status === 'CLOSED' ? 'default' : 'success'} size='small' />
          <LoadingButton variant='contained' loading={preparing} onClick={runPrepare} disabled={closed} sx={{ textTransform: 'none' }}>
            Prepare / refresh billing lines
          </LoadingButton>
        </CardContent>
      </Card>

      {configError && <Alert severity='error' sx={{ mb: 2 }}>{errText(configError, 'Could not load the billing settings.')}</Alert>}

      {waiting > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }} action={<Button color='inherit' size='small' onClick={() => navigate(`/ptl/billing/adhoc?state=INCOMPLETE&cycleMonth=${month}`)}>Complete them</Button>}>
          <b>{waiting} ad hoc {waiting === 1 ? "charge" : "charges"} incomplete</b>: {adhoc.waitingOps} waiting for the courier cost from Ops, {adhoc.waitingKam} waiting for the client amount
          from KAM. Finish them before {config ? dayOfNextMonth(month, closeDay) : 'the close day'}.
        </Alert>
      )}

      {summaryError && <Alert severity='error' sx={{ mb: 2 }}>{errText(summaryError, 'Could not load the billing for this month.')}</Alert>}
      {noLines && !closed && (
        <Alert severity='info' sx={{ mb: 2 }}>No billing lines for {monthLabel(month)} yet. Click <b>Prepare / refresh billing lines</b> to work them out.</Alert>
      )}
      {beforeStart && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Billing in Proship starts from <b>{monthLabel(config.firstBillingMonth)}</b> (Billing settings). {monthLabel(month)} was billed outside Proship,
          so it is closed here and none of its shipments are added to a Proship bill.
          {summary?.movedOut > 0 && ` ${summary.movedOut} ${summary.movedOut === 1 ? 'shipment was' : 'shipments were'} approved in a later month before this setting and stay there.`}
        </Alert>
      )}
      {closed && !beforeStart && summary?.movedOut > 0 && (
        <Alert severity='info' sx={{ mb: 2 }}>
          {monthLabel(month)} is closed. {summary.movedOut} {summary.movedOut === 1 ? 'shipment is' : 'shipments are'} billed in a later month
          (not approved by the close day, or delivered late). The numbers below are what stayed in {monthLabel(month)}.
        </Alert>
      )}

      <Box sx={{ ...kpiRowSx, mb: 2 }}>
        <KpiTile label='Billable shipments' value={summaryLoading ? '…' : summary?.shipments ?? '—'} sub={summary ? `${summary.clients} ${summary.clients === 1 ? 'client' : 'clients'}` : ''}
          onClick={() => navigate(`/ptl/billing/review?month=${month}`)} />
        <KpiTile label='Blocked' accent='error' value={summaryLoading ? '…' : summary?.blocked ?? '—'} sub='Cannot be approved until fixed'
          onClick={() => navigate(`/ptl/billing/review?month=${month}&blocked=true`)} />
        <KpiTile label='Billing (excl. GST)' accent='secondary' value={summaryLoading ? '…' : inr(summary?.totalExclGst, 0)}
          sub={summary ? `Base ${inr(summary.baseTotal, 0)} · DPH ${inr(summary.dhp, 0)} · Surcharges ${inr(summary.surchargeTotal, 0)}` : ''} />
        <KpiTile label='Margin (estimated)' accent='success' value={summaryLoading ? '…' : pct(margin)}
          sub={summary ? `${inr(summary.margin, 0)} on ${inr(summary.marginBaseTotal, 0)} billed with a known courier cost${summary.marginLeftOut ? ` · ${summary.marginLeftOut} left out (no price / no courier cost)` : ''}` : ''} />
        <KpiTile label='Ad hoc (client side)' accent='warning' value={inr(summary?.adhoc, 0)}
          sub={adhoc ? `${adhoc.charges} charges · courier side ${inr(adhoc.courierTotal, 0)}` : ''} onClick={() => navigate(`/ptl/billing/adhoc?cycleMonth=${month}`)} />
        <KpiTile label='In transit (not billed)' accent='error' value={transit?.count ?? '—'}
          sub={transit ? `${transit.redCount} older than ${transit.redFlagDays} days` : ''} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: 1, borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography sx={{ fontWeight: 600 }}>Not billed: in transit</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>Picked up in {monthLabel(month)} and not delivered or lost yet. Delivered after the cut-off moves to the next month.</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {(transit?.buckets || []).map((b) => (
                  <Box key={b.label} sx={{ border: 1, borderColor: b.red && b.count ? 'error.light' : 'divider', bgcolor: b.red && b.count ? '#FFF6F5' : 'transparent', borderRadius: 2, px: 1.25, py: 0.5, fontSize: 12 }}>
                    <b style={{ fontSize: 15, marginRight: 4 }}>{b.count}</b>{b.label}
                  </Box>
                ))}
              </Box>
              <Table size='small'>
                <TableHead><TableRow><TableCell>AWB</TableCell><TableCell>Client</TableCell><TableCell>Courier</TableCell><TableCell>Picked up</TableCell><TableCell align='right'>Days</TableCell></TableRow></TableHead>
                <TableBody>
                  {(transit?.rows || []).map((r) => (
                    <TableRow key={r.awb} sx={r.red ? { bgcolor: '#FFF6F5' } : undefined}>
                      <TableCell sx={{ fontWeight: 500 }}>{r.awb}</TableCell><TableCell>{r.merchantName}</TableCell><TableCell>{r.courierName}</TableCell>
                      <TableCell>{fmtDate(r.pickupAt)}</TableCell>
                      <TableCell align='right'>{r.red ? <Chip size='small' color='error' label={`⚑ ${r.days}`} /> : r.days}</TableCell>
                    </TableRow>
                  ))}
                  {transit && transit.rows.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary' }}>Nothing in transit.</TableCell></TableRow>}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Red flag: in transit more than {transit?.redFlagDays ?? '—'} days after pickup (Billing settings).</Typography>
                <Button size='small' disabled={!transit?.count} sx={{ textTransform: 'none' }}
                  onClick={() => downloadFile(getDownload, `${apiConstants.BASE_LOCAL}${BILLING_BASE}/overview/${month}/in-transit.csv`, `PTL_InTransit_${month}.csv`)
                    .catch(() => enqueueSnackbar('Could not download the list.', { variant: 'error' }))}>Download full list</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: 1, borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography sx={{ fontWeight: 600 }}>Loss-making and low-margin clients</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>From the billing lines with a known courier cost (estimated). Loss = below 0%, low = below {config ? `${config.lowMarginPct}%` : 'the low-margin %'} (Billing settings).</Typography>
              {losers.length === 0 && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No client below {config ? `${config.lowMarginPct}%` : 'the low-margin %'} margin this month.</Typography>}
              {losers.map((c) => (
                <Box key={c.merchantOid} sx={{ border: 1, borderColor: c.flag === 'LOSS' ? 'error.light' : 'warning.light', bgcolor: c.flag === 'LOSS' ? '#FFF8F7' : '#FFFCF2', borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><b>{c.merchantName}</b><Chip size='small' color={c.flag === 'LOSS' ? 'error' : 'warning'} label={c.flag === 'LOSS' ? 'LOSS' : 'LOW MARGIN'} /></Box>
                    <Typography sx={{ fontSize: 13, color: c.flag === 'LOSS' ? 'error.main' : undefined }}>{pct(c.marginPct)} · {inr(c.margin, 0)}</Typography>
                  </Box>
                  <Typography component='div' sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                    {c.worstLane && <div>Worst lane: <b>{c.worstLane.key}</b> ({c.worstLane.shipments} shipments, margin {inr(c.worstLane.margin, 0)})</div>}
                    {c.worstCourier && <div>Worst courier: <b>{c.worstCourier.key}</b> ({c.worstCourier.shipments} shipments, margin {inr(c.worstCourier.margin, 0)})</div>}
                    <div>{c.atMinFreight} of {c.shipments} shipments billed at minimum freight</div>
                    {Number(c.freightRisePct) > 0 && <div>To reach {config ? `${config.lowMarginPct}%` : 'the target'} margin: raise freight rates by about <b>{pct(c.freightRisePct)}</b> (≈ {inr(c.extraNeeded, 0)} more this month)</div>}
                  </Typography>
                  <Button size='small' onClick={() => navigate(`/ptl/billing/review?month=${month}&merchant=${c.merchantOid}&sort=margin_asc`)} sx={{ textTransform: 'none', p: 0, mt: 0.5 }}>View shipments ›</Button>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ border: 1, borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>What is blocking or needs review</Typography>
                <Button size='small' onClick={() => refetch()} sx={{ textTransform: 'none' }}>Refresh</Button>
              </Box>
              {summary && (
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                  Check: charge columns vs Proship's stored price, difference {inr(summary.reconciliationDifference)}
                  {Number(summary.reconciliationDifference) === 0 ? ' (OK)' : ' (charges without a breakup entry)'}.
                </Typography>
              )}
              {flagRows.length === 0 && !summaryLoading && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Nothing flagged.</Typography>}
              {flagRows.length > 0 && (
                <Table size='small'>
                  <TableHead><TableRow><TableCell>Reason</TableCell><TableCell align='right'>Shipments</TableCell></TableRow></TableHead>
                  <TableBody>
                    {flagRows.map((r) => (
                      <TableRow key={r.flag}>
                        <TableCell sx={{ fontSize: 13 }}>{r.message}</TableCell>
                        <TableCell align='right'>{r.n}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 2 }}>
            <CardContent>
              <Typography sx={{ fontWeight: 600 }}>Diesel price for {monthLabel(month)}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>Entered once a month. Drives DPH for clients with a DPH clause (Billing Terms).</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField size='small' type='number' label='Average diesel price (₹/L)' value={diesel} disabled={!billing || dieselLocked}
                  onChange={(e) => setDieselInput(e.target.value)} inputProps={{ step: '0.01', min: 0 }} />
                <LoadingButton variant='contained' loading={savingDiesel} disabled={!billing || diesel === '' || dieselLocked}
                  onClick={saveDiesel} sx={{ textTransform: 'none' }}>Save</LoadingButton>
              </Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
                {cycle?.dieselPrice != null ? `Saved ₹${cycle.dieselPrice}${cycle.updatedBy ? ` by ${cycle.updatedBy}, ${fmtDateTime(cycle.updatedAt)}` : ''}.` : 'Not entered yet: shipments of clients with a DPH clause are blocked.'}
                {dieselLocked && ' The month is closed, so it cannot change.'}
                {cycle?.status === 'CLOSED' && cycle?.dieselPrice == null && ' The month is closed, but its price can be entered once: its late shipments billed in later months need it.'}
                {!billing && ' Changing it needs PTL_BILLING access.'}
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 600 }}>Billing settings</Typography>
                {billing && config && <Button size='small' onClick={() => setSettingsOpen(true)} sx={{ textTransform: 'none' }}>Edit</Button>}
              </Box>
              {config && (
                <Table size='small'>
                  <TableBody>
                    {[
                      ['Billing in Proship starts from', config.firstBillingMonth ? monthLabel(config.firstBillingMonth) : 'Not set'],
                      ['Delivered by (day of next month)', config.cutoffDay],
                      ['Approve by (day of next month)', config.closeDay],
                      ['Max shipments / clients per approval', `${config.maxShipmentsPerApproval} / ${config.maxClientsPerApproval}`],
                      ['In-transit red flag after (days)', config.inTransitRedFlagDays],
                      ['Low margin below', `${config.lowMarginPct}%`],
                      ['Ad hoc wallet debit when balance is low', config.adhocWalletAllowNegative ? 'Debit anyway' : 'Refuse the charge'],
                      ['Agreement shown as expiring (days before expiry)', config.agreementExpiringDays ?? 'Not set'],
                      ['Weight slabs (kg)', (config.weightSlabs || []).map((w) => w.label).join(', ')],
                      ['Margin filter levels', (config.marginFilterPcts || []).length ? config.marginFilterPcts.map((x) => `${x}%`).join(', ') : 'Not set'],
                      ['Zone names', (config.zoneNames || []).length ? (config.zoneNames || []).map((z) => `${z.code} = ${z.name}`).join(', ') : 'Not set (codes shown)'],
                    ].map(([k, v]) => (
                      <TableRow key={k}><TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{k}</TableCell><TableCell align='right' sx={{ fontSize: 13 }}>{v}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {settingsOpen && <SettingsDialog config={config} onClose={() => setSettingsOpen(false)} />}
    </Box>
  )
}

function SettingsDialog({ config, onClose }) {
  const { enqueueSnackbar } = useSnackbar()
  const [save, { isLoading }] = useUpdateBillingConfigMutation()
  const [v, setV] = useState({
    cutoffDay: config.cutoffDay, closeDay: config.closeDay, maxShipmentsPerApproval: config.maxShipmentsPerApproval,
    maxClientsPerApproval: config.maxClientsPerApproval, inTransitRedFlagDays: config.inTransitRedFlagDays,
    lowMarginPct: config.lowMarginPct, lookbackDays: config.lookbackDays, adhocProofMaxMb: config.adhocProofMaxMb,
    adhocWalletAllowNegative: !!config.adhocWalletAllowNegative, agreementExpiringDays: config.agreementExpiringDays ?? '',
    firstBillingMonth: config.firstBillingMonth || '',
  })
  const [levelsText, setLevelsText] = useState((config.marginFilterPcts || []).join(', '))
  const [zonesText, setZonesText] = useState((config.zoneNames || []).map((z) => `${z.code} = ${z.name}`).join('\n'))
  const num = (k, label) => (
    <TextField key={k} size='small' type='number' label={label} value={v[k]} fullWidth
      onChange={(e) => setV({ ...v, [k]: e.target.value === '' ? '' : Number(e.target.value) })} />
  )
  const submit = async () => {
    // one zone per line: "2001 = C1" (also accepts a tab or comma, as pasted from a sheet)
    const zoneNames = []
    const lines = zonesText.split('\n').map((l) => l.trim()).filter(Boolean)
    // a header row pasted from a sheet (e.g. "Zone  Zone 1": no zone code in it) is skipped; any other bad line is refused
    if (lines.length && !/\d{3,}/.test(lines[0])) lines.shift()
    for (const line of lines) {
      const m = line.match(/^(\d+)\s*[=,\t ]\s*(.+)$/)
      if (!m) return enqueueSnackbar(`Zone line "${line}" is not "code = name".`, { variant: 'error' })
      zoneNames.push({ code: m[1], name: m[2].trim() })
    }
    const marginFilterPcts = levelsText.split(/[,\s]+/).filter(Boolean).map(Number)
    if (marginFilterPcts.some((x) => !Number.isInteger(x) || x < 1 || x > 99)) return enqueueSnackbar('Margin filter levels must be whole numbers from 1 to 99, e.g. 5, 10.', { variant: 'error' })
    const res = await save({ ...v, zoneNames, marginFilterPcts, agreementExpiringDays: v.agreementExpiringDays === '' ? undefined : v.agreementExpiringDays })
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    enqueueSnackbar('Billing settings saved', { variant: 'success' })
    onClose()
  }
  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Billing settings</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important', gridTemplateColumns: { sm: '1fr 1fr' } }}>
        <TextField sx={{ gridColumn: '1 / -1' }} size='small' type='month' label='Billing in Proship starts from (service month)' value={v.firstBillingMonth}
          onChange={(e) => setV({ ...v, firstBillingMonth: e.target.value })} InputLabelProps={{ shrink: true }}
          helperText='Earlier months were billed outside Proship: they stay closed and their shipments never go on a Proship bill.' />
        {num('cutoffDay', 'Delivered by (day of next month)')}
        {num('closeDay', 'Approve by (day of next month)')}
        {num('maxShipmentsPerApproval', 'Max shipments per approval')}
        {num('maxClientsPerApproval', 'Max clients per approval')}
        {num('inTransitRedFlagDays', 'In-transit red flag (days)')}
        {num('lowMarginPct', 'Low margin below (%)')}
        {num('lookbackDays', 'Look-back (days)')}
        {num('adhocProofMaxMb', 'Approval file max (MB)')}
        {num('agreementExpiringDays', 'Agreement "expiring" window (days)')}
        <FormControlLabel sx={{ gridColumn: '1 / -1' }} control={<Switch checked={v.adhocWalletAllowNegative}
          onChange={(e) => setV({ ...v, adhocWalletAllowNegative: e.target.checked })} />}
          label='Prepaid client: debit an ad hoc charge even if the wallet goes below zero' />
        <TextField sx={{ gridColumn: '1 / -1' }} size='small' label='Margin filter levels (%)' value={levelsText} onChange={(e) => setLevelsText(e.target.value)}
          placeholder='5, 10' InputLabelProps={{ shrink: true }} helperText='Extra "Below x%" choices in the Billing Review margin filter; the low-margin % is always there.' />
        <TextField sx={{ gridColumn: '1 / -1' }} size='small' multiline minRows={4} maxRows={12} label='Zone names (one per line: code = name)'
          value={zonesText} onChange={(e) => setZonesText(e.target.value)} placeholder={'2001 = C1\n3001 = W1'} InputLabelProps={{ shrink: true }}
          helperText='Proship stores B2B zones as codes (from the zone upload). Names here are shown on the billing screens and in the billing files; a code without a name is shown as the code.' />
        <Typography sx={{ gridColumn: '1 / -1', fontSize: 12, color: 'text.secondary' }}>Every change is recorded in the audit history.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton variant='contained' loading={isLoading} onClick={submit}>Save</LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
